import type { DayResult, JobFlight } from '../../../src/types.ts';

export type MaxStops = number | null;

export function filterFlights(flights: JobFlight[] | undefined, maxStops: MaxStops): JobFlight[] {
  const list = flights ?? [];
  if (maxStops == null) return list;
  return list.filter((f) => (f.stops ?? 0) <= maxStops);
}

export function cheapestOf(flights: JobFlight[]): JobFlight | null {
  let min: JobFlight | null = null;
  for (const f of flights) if (min == null || f.price < min.price) min = f;
  return min;
}

export function dayCheapest(day: DayResult, maxStops: MaxStops): JobFlight | null {
  return cheapestOf(filterFlights(day.flights, maxStops));
}

export interface PriceBands {
  min: number | null;
  p25: number | null;
  p75: number | null;
}

/** Quartile thresholds used to colour-grade the calendar. */
export function priceBands(prices: number[]): PriceBands {
  if (!prices.length) return { min: null, p25: null, p75: null };
  const sorted = [...prices].sort((a, b) => a - b);
  return {
    min: sorted[0] ?? null,
    p25: sorted[Math.floor(sorted.length * 0.25)] ?? null,
    p75: sorted[Math.floor(sorted.length * 0.75)] ?? null,
  };
}

export interface AirlineBest extends JobFlight {
  date: string;
}

/** Cheapest flight per airline across the whole month, ascending by price. */
export function cheapestPerAirline(days: DayResult[], maxStops: MaxStops): AirlineBest[] {
  const byAirline = new Map<string, AirlineBest>();
  for (const day of days) {
    for (const f of filterFlights(day.flights, maxStops)) {
      const key = f.airlineCode || f.airline || 'unknown';
      const cur = byAirline.get(key);
      if (!cur || f.price < cur.price) byAirline.set(key, { ...f, date: day.date });
    }
  }
  return [...byAirline.values()].sort((a, b) => a.price - b.price);
}
